import React, { useCallback, useContext, useState } from "react";
import { Link } from "gatsby";
import { useLocation } from "@reach/router";
import { Helmet } from "react-helmet";
import clsx from "clsx";
import PropTypes from "prop-types";
import { routes } from "../../routes";
import useTranslations from "../../hoc/useTranslations";
import { LocaleContext } from "../Layout/Layout";
import Button from "../Button/Button";
import { getPathName, setPreferedLanguage } from "../../helpers/helpers";

import LogoSVG from "../../assets/icons/botwise-logo.svg";
import LinkedinSVG from "../../assets/icons/header_linkedin.svg";
import PhoneSVG from "../../assets/icons/header_phone.svg";
import MailSVG from "../../assets/icons/header_mail.svg";

import "./Nav.scss";

const Nav = ({ toggleModal, isPolish }) => {
	const { locale } = useContext(LocaleContext);
	const [isMenuOpened, setIsMenuOpened] = useState(false);
	const { pathname } = useLocation();
	const {
		menu: { cc_report, linkedin_profile, contact, product },
		getDemoButton,
	} = useTranslations();

	const toggleMenu = () => setIsMenuOpened(!isMenuOpened);

	const changeLocale = () => {
		setPreferedLanguage(locale === "en" ? "pl" : "en");
	};

	const isItemActive = useCallback(
		path => {
			if (
				pathname.includes(routes[locale].blogTag + "/case-study/") &&
				!path.includes(routes[locale].blogTag + "/case-study/")
			)
				return false;

			return pathname.includes(path);
		},
		[pathname, routes, locale],
	);

	return (
		<nav className={clsx("navigation", isMenuOpened && "navigation--opened")}>
			<Helmet>
				<html className={clsx(isMenuOpened && "scrollOff")} />
				<body className={clsx(isMenuOpened && "scrollOff")} />
				<meta name="format-detection" content="telephone=no" />
			</Helmet>
			<div className="navigation__container">
				<ul className="navigation__list navigation__list--logoContainer">
					<li className="navigation__list--item">
						<Link
							className="link navigation__list--logo navigation__list--link"
							to={routes[locale].home}
						>
							<LogoSVG className="navigation__list--icon" />
						</Link>
					</li>
				</ul>
				<div className={clsx("navigation__menu", isMenuOpened && "navigation__menu--opened")}>
					<ul className="navigation__list">
						<li
							className={clsx(
								"navigation__list--item",
								isItemActive(routes[locale].product) && "active",
							)}
						>
							<a className="link navigation__list--link" href={routes[locale].product}>
								{product}
							</a>
						</li>
						<li
							className={clsx(
								"navigation__list--item",
								isItemActive(routes[locale].blogTag + "/case-study/") && "active",
							)}
						>
							<a
								className="link navigation__list--link"
								href={routes[locale].blogTag + "/case-study/?redirect=true"}
							>
								Case Study
							</a>
						</li>
						<li
							className={clsx(
								"navigation__list--item",
								isItemActive(routes[locale].blog) && "active",
							)}
						>
							<Link className="link navigation__list--link" to={routes[locale].blog}>
								Blog
							</Link>
						</li>
						<li className="navigation__list--item contact" tabIndex="0">
							<div className="nav-contact">{contact}</div>
							<div className="navigation__list--item nav-contact__container">
								<a
									className="link navigation__list--link nav-contact__container--item phone"
									href="tel:+48601789982"
								>
									<div className="nav-contact__container--item-icon">
										<PhoneSVG />
									</div>
									+48 601 789 982
								</a>
								<a
									className="link navigation__list--link nav-contact__container--item"
									href={`mailto:${isPolish ? "kontakt" : "contact"}@botwise.io`}
								>
									<div className="nav-contact__container--item-icon">
										<MailSVG />
									</div>
									{`${isPolish ? "kontakt" : "contact"}@botwise.io`}
								</a>
								<a
									href="https://www.linkedin.com/company/botwise/"
									rel="noopener noreferrer"
									target="_blank"
									className="link navigation__list--link nav-contact__container--item"
								>
									<div className="nav-contact__container--item-icon">
										<LinkedinSVG />
									</div>
									{linkedin_profile}
								</a>
							</div>
						</li>
						{isPolish && (
							<li
								className={clsx(
									"navigation__list--item special",
									isItemActive(routes[locale].ccReport2020) && "active",
								)}
							>
								<Link
									className="link navigation__list--link navigation__list--link-special"
									to={routes[locale].ccReport2020}
								>
									{cc_report}
								</Link>
							</li>
						)}
						<li className="navigation__list--item language">
							<Link
								to={getPathName(pathname, locale === "en" ? "pl" : "en")}
								onClick={changeLocale}
								className="link navigation__language desktop"
							>
								{locale === "en" ? "PL" : "EN"}
							</Link>
						</li>
						<li className="navigation__list--item cta-button">
							<Button onClick={() => toggleModal("TopNav CTA")}>{getDemoButton}</Button>
						</li>
					</ul>
				</div>
				<Link
					to={getPathName(pathname, locale === "en" ? "pl" : "en")}
					onClick={changeLocale}
					className="link navigation__language mobile"
				>
					{locale === "en" ? "PL" : "EN"}
				</Link>
				<button
					onClick={toggleMenu}
					className={clsx("navigation__button", isMenuOpened && "navigation__button--opened")}
				>
					<span className="navigation__button--hamburger"></span>
				</button>
			</div>
		</nav>
	);
};

Nav.propTypes = {
	toggleModal: PropTypes.func.isRequired,
	isPolish: PropTypes.bool.isRequired,
};

export default Nav;
